/**
 * scopeHierarchy — résolution PURE du périmètre effectif d'un utilisateur
 * (Lot B du chantier « Cohérence rôles dynamiques ↔ routage des cockpits ↔
 * périmètres hiérarchiques », 2026-07-16). Zéro accès réseau, zéro React —
 * l'accès aux données vit exclusivement dans scopeAccess.js (séparation
 * accès/calcul imposée par la revue du plan).
 *
 * Règle de repli STRICTE PAR MODE (jamais un repli silencieux plus large) :
 *   legacy  → périmètre plat profiles.campus_id/school_id/... (comportement
 *             actuel de useGovAccess().scopeFilter(), inchangé)
 *   dynamic → user_role_assignments UNIQUEMENT ; aucune affectation active
 *             = AUCUN accès (fail-closed). Un tenant déjà dynamic ne retombe
 *             JAMAIS sur profiles.* parce qu'une affectation a expiré.
 *
 * Le graphe tenant_scopes est parcouru parent → descendants avec défenses
 * explicites : limite de profondeur, détection de cycle, nœuds inactifs
 * ignorés, doublons dédupliqués, affectations orphelines (nœud absent du
 * graphe) limitées à leur propre nœud sans descendance (exactitude, pas
 * élargissement) et signalées dans `orphans` pour diagnostic.
 */

/** Ordre hiérarchique de référence (nœuds facultatifs — une école peut être
 * rattachée directement au campus sans faculté). Le 15e type `cycle` est
 * distinct de `track` (migration 20260716111000). */
export const SCOPE_TYPES = [
  'tenant', 'campus', 'faculty', 'school', 'program', 'track',
  'cycle', 'level', 'class', 'project', 'research_center',
  'laboratory', 'branch', 'country', 'region',
];

/** Profondeur maximale de descente dans tenant_scopes — un graphe légitime
 * (tenant→campus→faculty→school→program→track→cycle→level→class) tient en
 * 9 niveaux ; 32 laisse une marge large tout en garantissant l'arrêt sur un
 * graphe mal formé. Miroir JS de la limite WITH RECURSIVE prévue côté
 * Postgres (user_has_scope_access, Lot D2). */
export const MAX_SCOPE_DEPTH = 32;

const nodeKey = (scopeType, scopeId) => `${scopeType}:${scopeId}`;

/** Une affectation est-elle réellement active à l'instant `now` ?
 * Respecte LES DEUX bornes de délégation (delegate_start ET delegate_end —
 * correction explicite de la revue : pas seulement la fin). Une affectation
 * sans `role_id` (orpheline après un ON DELETE SET NULL historique) n'accorde
 * RIEN — checklist sécurité du plan §3. */
export function isAssignmentActive(assignment, now = new Date()) {
  if (!assignment || assignment.is_active === false) return false;
  if (!assignment.role_id) return false;
  if (assignment.delegate_start) {
    const start = new Date(assignment.delegate_start);
    if (!Number.isNaN(start.getTime()) && now < start) return false;
  }
  if (assignment.delegate_end) {
    const end = new Date(assignment.delegate_end);
    if (!Number.isNaN(end.getTime()) && now > end) return false;
  }
  return true;
}

/** Index enfants du graphe tenant_scopes : clé parent → [nœuds enfants actifs]. */
function buildChildrenIndex(scopeGraph) {
  const children = new Map();
  for (const node of scopeGraph) {
    if (node.is_active === false) continue;
    if (!node.parent_scope_type || !node.parent_scope_id) continue;
    const key = nodeKey(node.parent_scope_type, node.parent_scope_id);
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(node);
  }
  return children;
}

/** Descend le sous-arbre d'un nœud (lui inclus) avec défenses cycle/profondeur. */
function collectDescendants(rootType, rootId, childrenIndex, nodesByKey) {
  const collected = new Map();
  const visited = new Set();
  const stack = [{ scopeType: rootType, scopeId: rootId, depth: 0 }];
  let truncated = false;

  while (stack.length > 0) {
    const { scopeType, scopeId, depth } = stack.pop();
    const key = nodeKey(scopeType, scopeId);
    if (visited.has(key)) continue; // cycle ou chemin déjà couvert
    visited.add(key);
    if (depth > MAX_SCOPE_DEPTH) { truncated = true; continue; }

    const node = nodesByKey.get(key);
    collected.set(key, {
      scope_type: scopeType,
      scope_id: scopeId,
      label: node?.label ?? null,
    });

    for (const child of childrenIndex.get(key) || []) {
      stack.push({ scopeType: child.scope_type, scopeId: child.scope_id, depth: depth + 1 });
    }
  }
  return { collected, truncated };
}

/** Périmètre legacy plat — réplique la sémantique EXACTE de
 * useGovAccess().scopeFilter() : premier champ non nul dans l'ordre
 * campus > school > country > region > branch ; aucun champ → tenant complet. */
function resolveLegacyScopes(user) {
  const flat = [
    ['campus', user?.campus_id],
    ['school', user?.school_id],
    ['country', user?.country_id],
    ['region', user?.region_id],
    ['branch', user?.branch_id],
  ];
  for (const [scopeType, scopeId] of flat) {
    if (scopeId) {
      return {
        mode: 'legacy',
        full: false,
        scopes: [{ scope_type: scopeType, scope_id: scopeId, label: null }],
        orphans: [],
        truncated: false,
      };
    }
  }
  return { mode: 'legacy', full: true, scopes: [], orphans: [], truncated: false };
}

/**
 * Résout le périmètre effectif d'un utilisateur à partir d'un contexte déjà
 * chargé (scopeAccess.js::loadScopeContext). Pur, injectable, testable.
 *
 * @param {{
 *   mode: 'legacy'|'dynamic',
 *   user?: { campus_id?, school_id?, country_id?, region_id?, branch_id? },
 *   assignments?: Array<{ role_id, scope_type, scope_id, is_active,
 *     delegate_start?, delegate_end? }>,
 *   scopeGraph?: Array<{ scope_type, scope_id, label?, parent_scope_type?,
 *     parent_scope_id?, is_active? }>,
 *   now?: Date,
 * }} scopeContext
 * @returns {{ mode: string, full: boolean,
 *   scopes: Array<{ scope_type, scope_id, label }>,
 *   orphans: Array<{ scope_type, scope_id }>, truncated: boolean }}
 *   `full: true` = accès tenant-complet (aucun filtre à appliquer) ;
 *   `full: false` + `scopes: []` = AUCUN accès (fail-closed dynamic).
 */
export function resolveEffectiveScopes(scopeContext = {}) {
  const { mode = 'legacy', user = null, assignments = [], scopeGraph = [], now = new Date() } = scopeContext;

  if (mode !== 'dynamic') return resolveLegacyScopes(user);

  const active = (assignments || []).filter((a) => isAssignmentActive(a, now));
  // Fail-closed : tenant dynamic sans affectation active = aucun accès,
  // jamais un repli sur profiles.* (élargissement silencieux interdit).
  if (active.length === 0) {
    return { mode: 'dynamic', full: false, scopes: [], orphans: [], truncated: false };
  }

  // Une affectation au niveau tenant donne le périmètre complet — inutile de
  // descendre le graphe.
  if (active.some((a) => a.scope_type === 'tenant' || !a.scope_type)) {
    return { mode: 'dynamic', full: true, scopes: [], orphans: [], truncated: false };
  }

  const activeGraph = (scopeGraph || []).filter((n) => n.is_active !== false);
  const nodesByKey = new Map(activeGraph.map((n) => [nodeKey(n.scope_type, n.scope_id), n]));
  const childrenIndex = buildChildrenIndex(activeGraph);

  const effective = new Map();
  const orphans = [];
  let truncated = false;

  for (const assignment of active) {
    if (!assignment.scope_id) continue; // scope non-tenant sans id : ininterprétable, n'accorde rien
    const key = nodeKey(assignment.scope_type, assignment.scope_id);
    if (!nodesByKey.has(key)) {
      // Nœud absent du graphe : accès limité au nœud exact de l'affectation,
      // aucune descendance devinée — signalé pour diagnostic (le Lot C rejette
      // ces écritures à la source).
      orphans.push({ scope_type: assignment.scope_type, scope_id: assignment.scope_id });
      if (!effective.has(key)) {
        effective.set(key, { scope_type: assignment.scope_type, scope_id: assignment.scope_id, label: null });
      }
      continue;
    }
    const result = collectDescendants(assignment.scope_type, assignment.scope_id, childrenIndex, nodesByKey);
    truncated = truncated || result.truncated;
    for (const [k, v] of result.collected) {
      if (!effective.has(k)) effective.set(k, v);
    }
  }

  return {
    mode: 'dynamic',
    full: false,
    scopes: [...effective.values()],
    orphans,
    truncated,
  };
}

/** Rang de largeur d'un scope_type (0 = tenant complet, croissant = plus
 * étroit) — mirroir JS de `scope_type_rank()` (migration 20261012150000).
 * `campus`/`branch`/`country`/`region` partagent le rang 1 : ce sont des
 * racines alternatives du périmètre legacy plat (resolveLegacyScopes), pas
 * des niveaux imbriqués les uns dans les autres. `null` pour un type inconnu
 * (jamais utilisé pour autoriser silencieusement un périmètre plus large). */
const SCOPE_RANK = {
  tenant: 0, campus: 1, branch: 1, country: 1, region: 1,
  faculty: 2, school: 3, program: 4, track: 5, cycle: 6, level: 7, class: 8,
  project: 9, research_center: 9, laboratory: 9,
};

export function scopeTypeRank(scopeType) {
  return Object.prototype.hasOwnProperty.call(SCOPE_RANK, scopeType) ? SCOPE_RANK[scopeType] : null;
}

/** `scopeType` respecte-t-il la portée maximale `maxScope` déclarée par un
 * rôle (tenant_roles.max_scope) ? Un type ou une portée inconnue n'autorise
 * jamais rien par défaut (fail-closed — même doctrine que le reste du
 * fichier) : un `maxScope` absent (role non trouvé) retourne `true` pour ne
 * pas bloquer les rôles qui n'en déclarent pas encore. */
export function scopeTypeWithinMax(scopeType, maxScope) {
  if (!maxScope) return true;
  const typeRank = scopeTypeRank(scopeType);
  const maxRank = scopeTypeRank(maxScope);
  if (typeRank === null || maxRank === null) return false;
  return typeRank >= maxRank;
}

/** Un périmètre effectif couvre-t-il un nœud donné ? Utilitaire pour les
 * vues consommatrices (Lot E) — `full` couvre tout, sinon appartenance
 * exacte au set résolu (les descendants y sont déjà matérialisés). */
export function scopeCovers(effectiveScopes, scopeType, scopeId) {
  if (!effectiveScopes) return false;
  if (effectiveScopes.full) return true;
  return effectiveScopes.scopes.some(
    (s) => s.scope_type === scopeType && s.scope_id === scopeId
  );
}
